import clsx from "clsx";

/**
 * Layout, spacing, radius, and extended typography tokens for Home, Review, and
 * agentic surfaces. Pair with surfaceTokens for colour roles.
 *
 * Must not import from surfaceTokens — surfaceTokens re-exports this module.
 */

/** Mirrors BORDER_DIVIDER in surfaceTokens (inlined to avoid circular import). */
const BORDER_DIVIDER = "border-slate-200 dark:border-slate-700/60";

/** Mirrors TEXT_MUTED in surfaceTokens (inlined to avoid circular import). */
const TEXT_MUTED = "text-slate-500 dark:text-slate-400";

// ── Page & grid spacing ─────────────────────────────────────────────────────

export const SPACE_PAGE = "p-3 sm:p-4";

export const GAP_SURFACE = "gap-3 sm:gap-4";

// ── Card & panel padding ────────────────────────────────────────────────────

/** Standard raised card / widget padding. */
export const PAD_CARD = "p-4 sm:p-5";

/** Nested inset block inside a card (tables, callouts, stat tiles). */
export const PAD_INSET = "p-3";

/** Header / footer chrome bars — horizontal padding aligns with card content. */
export const PAD_BAR = "px-4 py-3 sm:px-5";

/** Interactive list / timeline rows. */
export const PAD_ROW = "px-3 py-2.5";

// ── Vertical & inline rhythm ────────────────────────────────────────────────

export const GAP_STACK = "space-y-3";

export const GAP_STACK_LOOSE = "space-y-4";

export const GAP_INLINE = "gap-2";

export const GAP_INLINE_TIGHT = "gap-1.5";

// ── Card anatomy ──────────────────────────────────────────────────────────────

export const CARD_HEADER = "mb-3 flex min-h-7 items-center justify-between gap-3";

export const CARD_FOOTER = clsx("mt-4 border-t pt-3", BORDER_DIVIDER);

// ── Radius ladder ───────────────────────────────────────────────────────────

export const RADIUS_SURFACE = "rounded-2xl";

export const RADIUS_INSET = "rounded-xl";

export const RADIUS_ROW = "rounded-lg";

export const RADIUS_CHIP = "rounded-md";

// ── Extended typography ───────────────────────────────────────────────────────

/** Section eyebrow — use on every panel label. */
export const TEXT_EYEBROW = clsx(
  "text-xs font-medium uppercase tracking-wide",
  TEXT_MUTED,
);

/** Greeting eyebrow only (Home briefing). */
export const TEXT_EYEBROW_ACCENT =
  "text-xs font-medium uppercase tracking-wide text-indigo-600 dark:text-indigo-300/80";

/** Timestamps, table headers, meta lines. */
export const TEXT_META = clsx("text-xs", TEXT_MUTED);

/** Secondary body copy. */
export const TEXT_BODY_MUTED = clsx("text-sm", TEXT_MUTED);

/** Stat tile values. */
export const TEXT_STAT = "text-3xl font-bold sm:text-4xl";

// ── Layout widths ───────────────────────────────────────────────────────────

/** Review / task context rail — 320px on lg, 384px on xl. */
export const CONTEXT_RAIL_WIDTH = "lg:w-80 xl:w-96";

/** Theme-aware action bar / sticky footer background. */
export const SURFACE_ACTION_BAR = clsx(
  "border-t bg-white dark:bg-slate-900/90",
  BORDER_DIVIDER,
);
